"use client";

import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { ParallaxVisuals } from "./parallax-visuals";
import Image from "next/image";

export function ParallaxHero() {
  const { t } = useLanguage();

  return (
    <ParallaxVisuals>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center mt-12 sm:mt-0">
        {/* Left Column - Content */}
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left gap-6">
          {/* Title */}
          <h1 className="text-balance text-5xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl transition-colors duration-300 text-zinc-950 dark:text-white">
            {t.hero.title}
            <br />
            <span className="text-transparent text-2xl bg-clip-text bg-linear-to-r from-orange-400 to-orange-600 dark:from-orange-400 dark:to-blue-500 inline-block mt-2 pb-2 tracking-tighter leading-tight sm:text-4xl lg:text-5xl">
              {t.hero.subtitle}
            </span>
          </h1>

          {/* Description */}
          <p className="max-w-2xl text-lg sm:text-xl font-light transition-colors duration-300 text-zinc-700 dark:text-zinc-300">
            {t.hero.desc}
          </p>

          {/* Buttons */}
          <div className="mt-4 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4 w-full sm:w-auto">
            <a
              href="#uslugi"
              className="flex w-full sm:w-auto items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 bg-orange-600 shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:bg-orange-500 hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] dark:bg-orange-600 dark:shadow-[0_0_20px_rgba(249,115,22,0.3)] dark:hover:bg-orange-500 dark:hover:shadow-[0_0_30px_rgba(249,115,22,0.5)]"
            >
              {t.hero.btnServices}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#kontakt"
              className="flex w-full sm:w-auto items-center justify-center rounded-full backdrop-blur-md px-8 py-4 text-sm font-semibold border transition-all bg-zinc-900/5 text-zinc-900 border-zinc-900/10 hover:bg-zinc-900/10 dark:bg-white/10 dark:text-white dark:border-white/10 dark:hover:bg-white/20"
            >
              {t.hero.btnContact}
            </a>
          </div>
        </div>

        {/* Right Column - Partner Logos */}
        <div className="hidden lg:flex flex-col items-center justify-center pt-15 lg:pl-16 relative">
          <div className="w-full max-w-xl flex flex-col items-center">
            {/* Tag moved to become the section's badge/title */}
            <div className="mb-12 inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-medium backdrop-blur-md transition-all duration-300 border-orange-200 bg-orange-50/50 text-zinc-900 dark:border-white/10 dark:bg-black/10 dark:text-zinc-300 shadow-xl hover:bg-orange-100/50 dark:hover:bg-black/10">
              <span className="flex h-2 w-2 rounded-full mr-2 bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.8)] dark:bg-orange-500 dark:shadow-[0_0_10px_rgba(249,115,22,0.8)]"></span>
              {t.hero.tag}
            </div>

            <div className="grid grid-cols-2 gap-8 w-full">
              {([
                {
                  name: "Mazak",
                  href: "https://www.mazak.com/pl-pl/",
                  src: "/media/logos/Mazak_logo.svg",
                  imgH: "h-24",
                },
                {
                  name: "Blum Novotest",
                  href: "https://www.blum-novotest.com/pl/",
                  src: "/media/logos/blum-novotest_logo.svg",
                  srcLight: "/media/logos/blum-novotest_logo_light.svg",
                  imgH: "h-24",
                },
                {
                  name: "Mechatronika CNC",
                  href: "https://mechatronikacnc.pl/",
                  src: "/media/logos/mechatronika-logo.svg",
                  srcLight: "/media/logos/mechatronika-logo-light.svg",
                  imgH: "h-28",
                },
                {
                  name: "Robojob",
                  href: "https://robojob.eu/en/",
                  src: "/media/logos/robojob-logo.svg",
                  srcLight: "/media/logos/robojob-logo_light.svg",
                  imgH: "h-28",
                },
              ] as const).map((logo) => (
                <a
                  key={logo.name}
                  href={logo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex h-48 w-full items-center justify-center rounded-3xl bg-zinc-100/50 dark:bg-zinc-900/30 transition-all duration-500 hover:-translate-y-1.5 hover:bg-white/20 dark:hover:bg-black/60 hover:shadow-[0_20px_40px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
                >
                  <div className={`relative ${logo.imgH} w-4/5 flex items-center justify-center`}>
                    {"srcLight" in logo ? (
                      <>
                        <Image src={logo.src} alt={logo.name} fill className="object-contain opacity-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110 hidden dark:block" />
                        <Image src={logo.srcLight} alt={logo.name} fill className="object-contain opacity-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110 block dark:hidden" />
                      </>
                    ) : (
                      <Image src={logo.src} alt={logo.name} fill className="object-contain opacity-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110" />
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ParallaxVisuals>
  );
}
